import jsQR from 'jsqr';
import {
  MultiFormatReader,
  RGBLuminanceSource,
  BinaryBitmap,
  HybridBinarizer,
  GlobalHistogramBinarizer,
  BarcodeFormat,
  DecodeHintType,
} from '@zxing/library';

/**
 * Clean and extract UIBN or book title slug if the input string is a URL (e.g. from generated QR codes / barcodes)
 */
export const cleanUibnCode = (rawCode: string): string => {
  let cleanCode = rawCode.trim();

  // If the scanned code is a full book URL like https://uibn.web.id/buku/judul-buku
  if (cleanCode.includes('/buku/')) {
    const parts = cleanCode.split('/buku/');
    if (parts.length > 1) {
      const slugPart = parts[1].split(/[?#&]/)[0];
      const subParts = slugPart.split('/');
      cleanCode = decodeURIComponent(subParts[subParts.length - 1] || subParts[0]);
      return cleanCode.trim();
    }
  } else if (cleanCode.includes('/verify/')) {
    const parts = cleanCode.split('/verify/');
    if (parts.length > 1) {
      cleanCode = decodeURIComponent(parts[1]);
    }
  } else if (cleanCode.includes('#search=')) {
    const parts = cleanCode.split('#search=');
    if (parts.length > 1) {
      cleanCode = decodeURIComponent(parts[1]);
    }
  } else if (cleanCode.includes('search=')) {
    const parts = cleanCode.split('search=');
    if (parts.length > 1) {
      cleanCode = decodeURIComponent(parts[1]);
    }
  } else if (cleanCode.includes('code=')) {
    const parts = cleanCode.split('code=');
    if (parts.length > 1) {
      cleanCode = decodeURIComponent(parts[1]);
    }
  } else if (cleanCode.includes('verify=')) {
    const parts = cleanCode.split('verify=');
    if (parts.length > 1) {
      cleanCode = decodeURIComponent(parts[1]);
    }
  } else if (cleanCode.includes('uibn=')) {
    const parts = cleanCode.split('uibn=');
    if (parts.length > 1) {
      cleanCode = decodeURIComponent(parts[1]);
    }
  } else if (cleanCode.includes('UIBN-') || cleanCode.includes('uibn-')) {
    const match = cleanCode.match(/UIBN-\d+-\d+-\d+-[a-z0-9]+/i);
    if (match) {
      cleanCode = match[0];
    }
  } else if (/^(979|978)-\d+-\d+-[a-z0-9]+$/i.test(cleanCode)) {
    // If scanned barcode is compact format without prefix (e.g. 979-8042-1001-9), normalize to full UIBN-979-8042-1001-9
    cleanCode = `UIBN-${cleanCode.toUpperCase()}`;
  }

  // Strip out any trailing query string parameters or hashes
  const result = cleanCode.split(/[?#&]/)[0].trim();
  // If it's a standard UIBN, uppercase it; if it's a slug, keep lowercase
  return result.startsWith('UIBN-') || result.startsWith('uibn-') ? result.toUpperCase() : result;
};

/**
 * Calculates EAN-13, EAN-8, or UPC-A Modulo 10 Checksum
 */
export const validateEanChecksum = (code: string): boolean => {
  const digits = code.replace(/\D/g, '');
  if (digits.length !== 13 && digits.length !== 8 && digits.length !== 12) {
    return false;
  }
  let sum = 0;
  const reverseDigits = digits.split('').reverse().map(Number);
  
  for (let i = 0; i < reverseDigits.length; i++) {
    if (i === 0) {
      sum += reverseDigits[i];
    } else {
      const weight = (i % 2 === 1) ? 3 : 1;
      sum += reverseDigits[i] * weight;
    }
  }
  return sum % 10 === 0;
};

/**
 * Validates scanned candidate string to filter out false-positive artifact misdecodes
 */
export const isValidScannedResult = (rawCode: string | null | undefined): boolean => {
  if (!rawCode) return false;
  const clean = rawCode.trim();
  if (clean.length < 4) return false;

  // 1. Full UIBN or URL or book path -> Always valid
  if (clean.includes('UIBN-') || clean.includes('uibn-') || clean.startsWith('http://') || clean.startsWith('https://') || clean.includes('/buku/')) {
    return true;
  }

  // 2. Pure Numeric 1D Barcode check (EAN-13, EAN-8, UPC-A)
  const isNumericOnly = /^\d+$/.test(clean);
  if (isNumericOnly) {
    if (clean.length === 13 || clean.length === 8 || clean.length === 12) {
      return validateEanChecksum(clean);
    }
    // Discard partial/corrupted digit sequences (e.g. 5, 6, 7, 9, 10, 11 digits that fail checksum)
    return false;
  }

  // 3. Code-128 / Alphanumeric UIBN slug check (minimum 6 valid characters)
  if (clean.length >= 6) {
    return true;
  }

  return false;
};

// Singleton ZXing reader instances
let cachedZxingReader: MultiFormatReader | null = null;
let cachedOffscreenCanvas: HTMLCanvasElement | null = null;

const getHints = () => {
  const hints = new Map<DecodeHintType, any>();
  // Strictly target high-reliability book barcode formats to eliminate non-checksum noise (e.g. ITF/Codabar artifacts)
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.CODE_128,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.DATA_MATRIX,
  ]);
  // Maximize search depth & try harder on small, low-resolution or noisy barcodes
  hints.set(DecodeHintType.TRY_HARDER, true);
  // Explicitly allow non-ideal/photographed real-world frames
  hints.set(DecodeHintType.PURE_BARCODE, false);
  hints.set(DecodeHintType.CHARACTER_SET, 'UTF-8');
  return hints;
};

const getMultiFormatReader = (): MultiFormatReader => {
  if (!cachedZxingReader) {
    cachedZxingReader = new MultiFormatReader();
    cachedZxingReader.setHints(getHints());
  }
  return cachedZxingReader;
};

const getOffscreenCanvas = (): HTMLCanvasElement => {
  if (!cachedOffscreenCanvas) {
    cachedOffscreenCanvas = document.createElement('canvas');
  }
  return cachedOffscreenCanvas;
};

/**
 * Fast RGBA to 1-byte Grayscale Luminance Array converter with adaptive contrast boosting
 */
const convertRgbaToLuminance = (
  rgbaData: Uint8ClampedArray,
  width: number,
  height: number,
  contrastBoost: number = 1.30
): Uint8ClampedArray => {
  const len = width * height;
  const luminances = new Uint8ClampedArray(len);
  for (let i = 0, j = 0; j < len; i += 4, j++) {
    let gray = (rgbaData[i] * 306 + rgbaData[i + 1] * 601 + rgbaData[i + 2] * 117) >> 10;
    if (contrastBoost !== 1.0) {
      gray = Math.round(128 + (gray - 128) * contrastBoost);
      if (gray < 0) gray = 0;
      else if (gray > 255) gray = 255;
    }
    luminances[j] = gray;
  }
  return luminances;
};

/**
 * High-precision ZXing multi-engine barcode and QR decoder on a single ImageData frame.
 * Ensures strict state resets between frames and applies multiple binarization passes.
 */
/**
 * Fast Local Adaptive Thresholding for faint/micro barcodes & QR codes
 */
const applyAdaptiveThreshold = (luminances: Uint8ClampedArray, width: number, height: number): Uint8ClampedArray => {
  const binarized = new Uint8ClampedArray(width * height);
  const windowSize = 13;
  const halfWindow = Math.floor(windowSize / 2);

  for (let y = 0; y < height; y += 1) {
    const rowOffset = y * width;
    for (let x = 0; x < width; x += 1) {
      const idx = rowOffset + x;
      const pixelVal = luminances[idx];

      // Sample 5x5 sparse neighborhood
      let sum = 0;
      let count = 0;
      for (let dy = -halfWindow; dy <= halfWindow; dy += 3) {
        const ny = y + dy;
        if (ny >= 0 && ny < height) {
          const nRowOffset = ny * width;
          for (let dx = -halfWindow; dx <= halfWindow; dx += 3) {
            const nx = x + dx;
            if (nx >= 0 && nx < width) {
              sum += luminances[nRowOffset + nx];
              count++;
            }
          }
        }
      }
      const localAvg = count > 0 ? sum / count : 128;
      binarized[idx] = pixelVal < localAvg - 6 ? 0 : 255;
    }
  }
  return binarized;
};

export const decodeFromImageData = (imageData: ImageData): string | null => {
  const { width, height, data } = imageData;

  // 1. Fast jsQR Pass for 2D QR Codes
  try {
    const qr = jsQR(data, width, height, { inversionAttempts: 'attemptBoth' });
    if (qr && qr.data && isValidScannedResult(qr.data)) {
      return qr.data.trim();
    }
  } catch {
    // Continue
  }

  // 2. Prepare Grayscale Luminance Conversion for ZXing
  const luminances = convertRgbaToLuminance(data, width, height);
  const lumSource = new RGBLuminanceSource(luminances, width, height);
  const reader = getMultiFormatReader();

  // 3. ZXing GlobalHistogramBinarizer Pass (Superior precision for 1D Barcodes: Code 128, EAN-13, ISBN)
  try {
    const bitmapHist = new BinaryBitmap(new GlobalHistogramBinarizer(lumSource));
    const result = reader.decode(bitmapHist);
    if (result && isValidScannedResult(result.getText())) {
      return result.getText().trim();
    }
  } catch {
    // Ignore and proceed to next binarizer
  } finally {
    reader.reset();
  }

  // 4. ZXing HybridBinarizer Pass (Optimal for uneven lighting and shadows)
  try {
    const bitmapHybrid = new BinaryBitmap(new HybridBinarizer(lumSource));
    const result = reader.decode(bitmapHybrid);
    if (result && isValidScannedResult(result.getText())) {
      return result.getText().trim();
    }
  } catch {
    // Ignore and proceed to next binarizer
  } finally {
    reader.reset();
  }

  // 5. Adaptive Local Binarization Pass for Micro / Faint Barcodes & QR Codes
  if (width >= 40 && height >= 20) {
    try {
      const adaptiveLuminances = applyAdaptiveThreshold(luminances, width, height);
      const adaptiveSource = new RGBLuminanceSource(adaptiveLuminances, width, height);
      const bitmapAdaptive = new BinaryBitmap(new GlobalHistogramBinarizer(adaptiveSource));
      const result = reader.decode(bitmapAdaptive);
      if (result && isValidScannedResult(result.getText())) {
        return result.getText().trim();
      }
    } catch {
      // Ignore
    } finally {
      reader.reset();
    }
  }

  // 6. Horizontal Stretch Pass for Compressed 1D Barcodes (Dramatically improves small Code 128 / EAN-13 / ISBN)
  if (width >= 80 && height >= 25) {
    try {
      const stretchW = Math.round(width * 1.8);
      const stretchedLum = new Uint8ClampedArray(stretchW * height);
      for (let y = 0; y < height; y++) {
        const srcRowOffset = y * width;
        const dstRowOffset = y * stretchW;
        for (let x = 0; x < stretchW; x++) {
          const srcX = Math.min(width - 1, Math.floor(x / 1.8));
          stretchedLum[dstRowOffset + x] = luminances[srcRowOffset + srcX];
        }
      }
      const stretchedSource = new RGBLuminanceSource(stretchedLum, stretchW, height);
      const bitmapStretched1D = new BinaryBitmap(new GlobalHistogramBinarizer(stretchedSource));
      const result = reader.decode(bitmapStretched1D);
      if (result && isValidScannedResult(result.getText())) {
        return result.getText().trim();
      }
    } catch {
      // Ignore
    } finally {
      reader.reset();
    }
  }

  // 7. ZXing Contrast-Stretched Pass (Enhances low-contrast prints or glossy covers)
  try {
    let minLum = 255;
    let maxLum = 0;
    for (let i = 0; i < luminances.length; i += 8) {
      const v = luminances[i];
      if (v < minLum) minLum = v;
      if (v > maxLum) maxLum = v;
    }

    const range = maxLum - minLum;
    if (range > 15 && range < 180) {
      const stretchedLuminances = new Uint8ClampedArray(luminances.length);
      const scale = 255 / range;
      for (let i = 0; i < luminances.length; i++) {
        stretchedLuminances[i] = Math.min(255, Math.max(0, (luminances[i] - minLum) * scale));
      }
      const stretchedSource = new RGBLuminanceSource(stretchedLuminances, width, height);
      const bitmapStretched = new BinaryBitmap(new GlobalHistogramBinarizer(stretchedSource));
      const result = reader.decode(bitmapStretched);
      if (result && isValidScannedResult(result.getText())) {
        return result.getText().trim();
      }
    }
  } catch {
    // Ignore and proceed to next pass
  } finally {
    reader.reset();
  }

  // 8. ZXing Inverted Luminance Pass (For inverted or white-on-dark barcodes & QRs)
  try {
    const invLuminances = new Uint8ClampedArray(width * height);
    for (let i = 0; i < luminances.length; i++) {
      invLuminances[i] = 255 - luminances[i];
    }
    const invSource = new RGBLuminanceSource(invLuminances, width, height);
    const bitmapInv = new BinaryBitmap(new HybridBinarizer(invSource));
    const result = reader.decode(bitmapInv);
    if (result && isValidScannedResult(result.getText())) {
      return result.getText().trim();
    }
  } catch {
    // Ignore
  } finally {
    reader.reset();
  }

  // 9. Software Sharpening Pass for Soft/Blurry Camera Feeds (Enhances blurry/out-of-focus barcode lines)
  if (width >= 60 && height >= 20) {
    try {
      const sharpLum = new Uint8ClampedArray(width * height);
      for (let y = 1; y < height - 1; y++) {
        const rowOffset = y * width;
        for (let x = 1; x < width - 1; x++) {
          const idx = rowOffset + x;
          const val = luminances[idx] * 5 - (
            luminances[idx - width] +
            luminances[idx + width] +
            luminances[idx - 1] +
            luminances[idx + 1]
          );
          sharpLum[idx] = Math.min(255, Math.max(0, val));
        }
      }
      const sharpSource = new RGBLuminanceSource(sharpLum, width, height);
      const bitmapSharp = new BinaryBitmap(new GlobalHistogramBinarizer(sharpSource));
      const result = reader.decode(bitmapSharp);
      if (result && isValidScannedResult(result.getText())) {
        return result.getText().trim();
      }
    } catch {
      // Ignore
    } finally {
      reader.reset();
    }
  }

  return null;
};

/**
 * Detect barcode/QR from live video stream with multi-scale macro resolution.
 * Specifically optimized for tiny/small barcodes and QR codes using 1:1 sensor crops,
 * super-sampling, and adaptive contrast enhancement.
 */
export const detectBarcodeFromVideo = async (
  video: HTMLVideoElement,
  options?: { maxDurationMs?: number; zoomLevel?: number }
): Promise<string | null> => {
  if (!video || video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
    return null;
  }

  const startTime = performance.now();
  const maxDurationMs = options?.maxDurationMs || 100; // Default max 100ms per frame
  const vWidth = video.videoWidth;
  const vHeight = video.videoHeight;
  const zoomFactor = Math.max(1, options?.zoomLevel || 1);

  // 1. Native BarcodeDetector (Hardware Accelerated on Chrome/Android)
  if ('BarcodeDetector' in window) {
    try {
      const detector = new (window as any).BarcodeDetector({
        formats: ['qr_code', 'ean_13', 'code_128', 'ean_8', 'upc_a', 'upc_e', 'data_matrix'],
      });
      const detected = await detector.detect(video);
      if (detected && detected.length > 0 && detected[0].rawValue) {
        const candidate = detected[0].rawValue.trim();
        if (isValidScannedResult(candidate)) {
          return candidate;
        }
      }
    } catch {
      // Fallback to multi-pass JS engine
    }
  }

  if (performance.now() - startTime > maxDurationMs) {
    return null;
  }

  const canvas = getOffscreenCanvas();
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  // Set hardware-accelerated canvas contrast filter for maximum barcode line separation
  const applyContrastFilter = (context: CanvasRenderingContext2D) => {
    try {
      context.filter = 'contrast(1.40) brightness(1.08) saturate(1.10)';
    } catch {
      // Ignore if canvas filter is unsupported in older browser
    }
  };

  // PASS 0: Ultra-Micro 12% Center ROI (Magnified 3.2x with Laplacian Sharpening for Tiny Barcodes / QRs)
  try {
    const fraction = 0.15 / zoomFactor;
    const ultraW = Math.max(50, Math.round(vWidth * fraction));
    const ultraH = Math.max(50, Math.round(vHeight * fraction));
    const ultraX = Math.round((vWidth - ultraW) / 2);
    const ultraY = Math.round((vHeight - ultraH) / 2);

    const outUltraW = Math.round(ultraW * 3.2);
    const outUltraH = Math.round(ultraH * 3.2);
    canvas.width = outUltraW;
    canvas.height = outUltraH;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    applyContrastFilter(ctx);
    ctx.drawImage(video, ultraX, ultraY, ultraW, ultraH, 0, 0, outUltraW, outUltraH);

    const ultraData = ctx.getImageData(0, 0, outUltraW, outUltraH);
    const uPixels = ultraData.data;
    const origPixels = new Uint8ClampedArray(uPixels);

    // Apply 3x3 Laplacian Unsharp Mask Filter
    for (let y = 1; y < outUltraH - 1; y++) {
      for (let x = 1; x < outUltraW - 1; x++) {
        const dstIdx = (y * outUltraW + x) * 4;
        const centerIdx = dstIdx;
        const topIdx = ((y - 1) * outUltraW + x) * 4;
        const bottomIdx = ((y + 1) * outUltraW + x) * 4;
        const leftIdx = (y * outUltraW + (x - 1)) * 4;
        const rightIdx = (y * outUltraW + (x + 1)) * 4;

        for (let c = 0; c < 3; c++) {
          const val = origPixels[centerIdx + c] * 5 - (
            origPixels[topIdx + c] +
            origPixels[bottomIdx + c] +
            origPixels[leftIdx + c] +
            origPixels[rightIdx + c]
          );
          uPixels[dstIdx + c] = Math.min(255, Math.max(0, val));
        }
      }
    }

    const ultraResult = decodeFromImageData(ultraData);
    if (ultraResult) {
      return ultraResult;
    }
  } catch {
    // Continue
  }

  if (performance.now() - startTime > maxDurationMs) {
    return null;
  }

  // PASS 1: Medium Micro-Macro Center ROI (Central 30% / zoomFactor magnified 2.2x)
  try {
    const fraction = 0.30 / zoomFactor;
    const microW = Math.max(80, Math.round(vWidth * fraction));
    const microH = Math.max(80, Math.round(vHeight * fraction));
    const microX = Math.round((vWidth - microW) / 2);
    const microY = Math.round((vHeight - microH) / 2);

    const outW = Math.round(microW * 2.2);
    const outH = Math.round(microH * 2.2);
    canvas.width = outW;
    canvas.height = outH;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    applyContrastFilter(ctx);
    ctx.drawImage(video, microX, microY, microW, microH, 0, 0, outW, outH);

    const microData = ctx.getImageData(0, 0, outW, outH);
    const microResult = decodeFromImageData(microData);
    if (microResult) {
      return microResult;
    }
  } catch {
    // Continue
  }

  if (performance.now() - startTime > maxDurationMs) {
    return null;
  }

  // PASS 2: Standard Reticle Bounding Box (Central 55% / zoomFactor at 1:1 Native Resolution)
  try {
    const fraction = 0.55 / zoomFactor;
    const macroW = Math.max(120, Math.round(vWidth * fraction));
    const macroH = Math.max(120, Math.round(vHeight * fraction));
    const macroX = Math.round((vWidth - macroW) / 2);
    const macroY = Math.round((vHeight - macroH) / 2);

    canvas.width = macroW;
    canvas.height = macroH;
    applyContrastFilter(ctx);
    ctx.drawImage(video, macroX, macroY, macroW, macroH, 0, 0, macroW, macroH);

    const macroImageData = ctx.getImageData(0, 0, macroW, macroH);
    const macroResult = decodeFromImageData(macroImageData);
    if (macroResult) {
      return macroResult;
    }
  } catch {
    // Continue
  }

  if (performance.now() - startTime > maxDurationMs) {
    return null;
  }

  // PASS 3: Full-frame standard resolution (for large or wide-angle barcodes)
  try {
    const maxDim = 850;
    let targetW = vWidth;
    let targetH = vHeight;
    if (vWidth > maxDim || vHeight > maxDim) {
      const ratio = Math.min(maxDim / vWidth, maxDim / vHeight);
      targetW = Math.round(vWidth * ratio);
      targetH = Math.round(vHeight * ratio);
    }

    canvas.width = targetW;
    canvas.height = targetH;
    applyContrastFilter(ctx);
    ctx.drawImage(video, 0, 0, targetW, targetH);
    const fullImageData = ctx.getImageData(0, 0, targetW, targetH);
    const fullResult = decodeFromImageData(fullImageData);
    if (fullResult) {
      return fullResult;
    }
  } catch {
    // Continue
  }

  if (performance.now() - startTime > maxDurationMs) {
    return null;
  }

  // PASS 4: Panoramic 1D Barcode Band Crop with Adaptive Dynamic Binarization
  try {
    const sliceX = Math.round(vWidth * 0.08);
    const sliceY = Math.round(vHeight * 0.28);
    const sliceW = Math.round(vWidth * 0.84);
    const sliceH = Math.round(vHeight * 0.44);

    canvas.width = sliceW;
    canvas.height = sliceH;
    ctx.drawImage(video, sliceX, sliceY, sliceW, sliceH, 0, 0, sliceW, sliceH);

    const binData = ctx.getImageData(0, 0, sliceW, sliceH);
    const bPixels = binData.data;

    let sum = 0;
    let count = 0;
    for (let i = 0; i < bPixels.length; i += 4) {
      const gray = (bPixels[i] * 306 + bPixels[i + 1] * 601 + bPixels[i + 2] * 117) >> 10;
      sum += gray;
      count++;
    }
    const avg = sum / (count || 1);

    for (let i = 0; i < bPixels.length; i += 4) {
      const gray = (bPixels[i] * 306 + bPixels[i + 1] * 601 + bPixels[i + 2] * 117) >> 10;
      const binVal = gray < avg - 8 ? 0 : 255;
      bPixels[i] = binVal;
      bPixels[i + 1] = binVal;
      bPixels[i + 2] = binVal;
    }

    const binResult = decodeFromImageData(binData);
    if (binResult) {
      return binResult;
    }
  } catch {
    // Continue
  }

  return null;
};

/**
 * Decode barcode/QR from an image file with multi-scale crops and multi-angle rotation.
 * Specifically scans both whole image and macro center crops for small barcodes in high-res photos.
 */
export const decodeBarcodeFromImage = async (
  file: File,
  options?: { timeoutMs?: number; signal?: AbortSignal }
): Promise<string | null> => {
  const timeoutMs = options?.timeoutMs || 4000;
  const startTime = Date.now();
  const imageUrl = URL.createObjectURL(file);

  try {
    if (options?.signal?.aborted) {
      throw new Error('Analisis dibatalkan oleh pengguna');
    }

    const img = new Image();
    img.src = imageUrl;
    await img.decode();

    if (options?.signal?.aborted || Date.now() - startTime > timeoutMs) {
      return null;
    }

    // 1. Native BarcodeDetector
    if ('BarcodeDetector' in window) {
      try {
        const detector = new (window as any).BarcodeDetector({
          formats: ['qr_code', 'ean_13', 'code_128', 'code_39', 'data_matrix', 'itf', 'upc_a', 'upc_e'],
        });
        const detected = await detector.detect(img);
        if (detected && detected.length > 0 && detected[0].rawValue) {
          return detected[0].rawValue.trim();
        }
      } catch {
        // Fallback
      }
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    // Multi-scale image crops:
    // Crop 1: Full Image (0, 0, width, height)
    // Crop 2: Center 50% Macro Crop (ideal for small barcodes in high-megapixel photos)
    // Crop 3: Center 25% Tight Macro Crop
    const crops = [
      { x: 0, y: 0, w: img.width, h: img.height },
      { x: Math.round(img.width * 0.25), y: Math.round(img.height * 0.25), w: Math.round(img.width * 0.5), h: Math.round(img.height * 0.5) },
      { x: Math.round(img.width * 0.35), y: Math.round(img.height * 0.35), w: Math.round(img.width * 0.3), h: Math.round(img.height * 0.3) },
    ];

    const rotations = [0, 90, 270, 180];

    for (const crop of crops) {
      if (options?.signal?.aborted || Date.now() - startTime > timeoutMs) break;

      for (const rotation of rotations) {
        if (options?.signal?.aborted || Date.now() - startTime > timeoutMs) break;

        if (rotation === 0) {
          canvas.width = crop.w;
          canvas.height = crop.h;
          ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
        } else if (rotation === 90) {
          canvas.width = crop.h;
          canvas.height = crop.w;
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((90 * Math.PI) / 180);
          ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, -crop.w / 2, -crop.h / 2, crop.w, crop.h);
          ctx.restore();
        } else if (rotation === 270) {
          canvas.width = crop.h;
          canvas.height = crop.w;
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate((270 * Math.PI) / 180);
          ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, -crop.w / 2, -crop.h / 2, crop.w, crop.h);
          ctx.restore();
        } else if (rotation === 180) {
          canvas.width = crop.w;
          canvas.height = crop.h;
          ctx.save();
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.rotate(Math.PI);
          ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, -crop.w / 2, -crop.h / 2, crop.w, crop.h);
          ctx.restore();
        }

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = decodeFromImageData(imgData);
        if (code) {
          return code;
        }
      }
    }

    return null;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};
